import React from 'react';
import { object, string } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import {
  ExternalLink,
  IconSocialMediaFacebook,
  IconSocialMediaInstagram,
  IconSocialMediaTwitter,
} from '../../components';
import IconSocialMediaTiktok from '../../components/IconSocialMediaTiktok/IconSocialMediaTiktok';
import renderMarkdown from '../PageBuilder/markdownProcessor';
import { Link as MarkdownLink } from '../PageBuilder/Primitives/Link';

import css from './FooterContainer.module.css';

const SOCIAL_ICON_COMPONENTS = {
  facebook: IconSocialMediaFacebook,
  instagram: IconSocialMediaInstagram,
  twitter: IconSocialMediaTwitter,
  x: IconSocialMediaTwitter,
  tiktok: IconSocialMediaTiktok,
  tik_tok: IconSocialMediaTiktok,
};

const isNonEmptyString = value => typeof value === 'string' && value.trim().length > 0;

const normalizePlatform = platform => {
  return isNonEmptyString(platform)
    ? platform
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_')
    : '';
};

const platformLabel = platform => {
  if (!isNonEmptyString(platform)) {
    return 'External link';
  }

  return platform
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, match => match.toUpperCase());
};

const getFooterBlocks = footer => (Array.isArray(footer?.blocks) ? footer.blocks : []);
const getSocialMediaLinks = footer =>
  Array.isArray(footer?.socialMediaLinks) ? footer.socialMediaLinks : [];

const getFooterColumnCount = (footer, footerBlockCount) => {
  const numberOfColumns = Number(footer?.numberOfColumns);
  if (!Number.isFinite(numberOfColumns) || numberOfColumns < 1) {
    return footerBlockCount;
  }

  return Math.max(Math.floor(numberOfColumns), footerBlockCount);
};

const FooterMarkdownList = props => {
  const { className, ...otherProps } = props;
  return <ul className={classNames(css.list, className)} {...otherProps} />;
};

const FooterMarkdownListItem = props => {
  const { className, ...otherProps } = props;
  return <li className={classNames(css.listItem, className)} {...otherProps} />;
};

const FooterMarkdownParagraph = props => {
  const { className, ...otherProps } = props;
  return <p className={classNames(css.markdownParagraph, className)} {...otherProps} />;
};

const FooterMarkdownLink = props => <MarkdownLink {...props} rootClassName={css.link} />;

const FOOTER_MARKDOWN_COMPONENTS = {
  ul: FooterMarkdownList,
  ol: FooterMarkdownList,
  li: FooterMarkdownListItem,
  p: FooterMarkdownParagraph,
  a: FooterMarkdownLink,
};

const renderFooterMarkdown = content => {
  if (!isNonEmptyString(content)) {
    return null;
  }

  try {
    return renderMarkdown(content, FOOTER_MARKDOWN_COMPONENTS);
  } catch {
    return null;
  }
};

const renderSocialMediaLink = (item, index) => {
  const link = item?.link || item;
  const href = link?.url;

  if (!isNonEmptyString(href)) {
    return null;
  }

  const platform = link?.platform;
  const normalizedPlatform = normalizePlatform(platform);
  const IconComponent = SOCIAL_ICON_COMPONENTS[normalizedPlatform];
  const label = platformLabel(platform);

  return (
    <ExternalLink
      key={`${normalizedPlatform || 'social'}-${href}-${index}`}
      href={href}
      className={classNames(css.icon, { [css.textSocialIcon]: !IconComponent })}
      title={label}
      aria-label={label}
    >
      {IconComponent ? <IconComponent /> : <span className={css.socialText}>{label}</span>}
    </ExternalLink>
  );
};

const FooterBlock = props => {
  const { block } = props;
  const title = block?.blockName;
  const markdownContent = block?.text?.content;
  const renderedContent = renderFooterMarkdown(markdownContent);

  if (!isNonEmptyString(title) && !renderedContent) {
    return null;
  }

  return (
    <div className={css.footerBlock}>
      {isNonEmptyString(title) ? <h2 className={css.footerBlockTitle}>{title}</h2> : null}
      {renderedContent}
    </div>
  );
};

FooterBlock.propTypes = {
  block: object.isRequired,
};

function FooterContainer(props) {
  const config = useConfiguration();
  const footer = config?.footer || {};
  const { rootClassName = null, className = null } = props;
  const classes = classNames(rootClassName || css.root, className);

  const footerBlocks = getFooterBlocks(footer);
  const renderableFooterBlocks = footerBlocks.filter(block => {
    return (
      block &&
      typeof block === 'object' &&
      (isNonEmptyString(block.blockName) || isNonEmptyString(block?.text?.content))
    );
  });
  const socialMediaLinks = getSocialMediaLinks(footer)
    .map(renderSocialMediaLink)
    .filter(Boolean);
  const footerColumnCount = getFooterColumnCount(footer, renderableFooterBlocks.length);
  const footerColumnsStyle = footerColumnCount
    ? { '--footer-column-count': String(footerColumnCount) }
    : null;

  const slogan = footer?.slogan?.content;
  const copyright = footer?.copyright?.content;
  const hasSlogan = isNonEmptyString(slogan);
  const hasCopyright = isNonEmptyString(copyright);
  const hasSocialMediaLinks = socialMediaLinks.length > 0;
  const hasOrganizationInfo = hasSlogan || hasCopyright;
  const hasFooterBlocks = renderableFooterBlocks.length > 0;

  if (!hasOrganizationInfo && !hasFooterBlocks && !hasSocialMediaLinks) {
    return null;
  }

  return (
    <div className={classes}>
      <div className={css.topBorderWrapper}>
        <div className={css.content}>
          {hasSocialMediaLinks ? (
            <div className={css.socialLinksMobile}>{socialMediaLinks}</div>
          ) : null}
          <div className={css.links}>
            {hasOrganizationInfo ? (
              <div className={css.organization} id="organization">
                <div className={css.organizationInfo}>
                  {hasSlogan ? <p className={css.organizationDescription}>{slogan}</p> : null}
                  {hasCopyright ? <p className={css.organizationCopyright}>{copyright}</p> : null}
                </div>
              </div>
            ) : null}
            {hasFooterBlocks ? (
              <div className={css.footerBlocks} style={footerColumnsStyle}>
                {renderableFooterBlocks.map((block, index) => (
                  <FooterBlock block={block} key={block?.blockId || index} />
                ))}
              </div>
            ) : null}
            {hasSocialMediaLinks ? (
              <div className={css.extraLinks}>
                <div className={css.socialLinks}>{socialMediaLinks}</div>
              </div>
            ) : null}
          </div>
          {hasCopyright ? (
            <div className={css.copyrightAndTermsMobile}>
              <p className={css.organizationCopyrightMobile}>{copyright}</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

FooterContainer.propTypes = {
  rootClassName: string,
  className: string,
};

export default FooterContainer;
